import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../../lib/api";
import { ChevronLeft, Truck, ExternalLink, Copy, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all"); // all | razorpay | cod
  const [fulfillmentFilter, setFulfillmentFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all"); // all | today | 7d | 30d | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
    setDeletingId(order.id);
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Order ${order.order_number} deleted`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const inDateRange = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (datePreset === "today") return d >= today;
    if (datePreset === "7d") {
      const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 7);
      return d >= cutoff;
    }
    if (datePreset === "30d") {
      const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 30);
      return d >= cutoff;
    }
    if (datePreset === "custom") {
      const from = customFrom ? new Date(customFrom) : null;
      const to = customTo ? new Date(customTo) : null;
      if (to) to.setHours(23, 59, 59, 999);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    }
    return true;
  };

  const filtered = orders.filter((o) => {
    if (paymentFilter !== "all" && o.payment_status !== paymentFilter) return false;
    if (methodFilter !== "all" && (o.payment_method || "").toLowerCase() !== methodFilter) return false;
    if (fulfillmentFilter !== "all" && (o.fulfillment_status || "unfulfilled") !== fulfillmentFilter) return false;
    if (datePreset !== "all" && !inDateRange(o.created_at)) return false;
    return true;
  });

  return (
    <div className="p-6 md:p-10" data-testid="admin-orders">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <span className="font-script text-3xl text-blush-400">the orders</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Orders</h1>
          <p className="mt-1 font-body text-xs text-ink-500" data-testid="orders-count">{filtered.length} of {orders.length} shown</p>
        </div>
      </header>

      {/* Filter rows */}
      <div className="space-y-3 mb-6">
        <FilterRow label="Payment">
          {["all", "paid", "pending", "failed"].map((s) => (
            <Chip key={s} active={paymentFilter === s} onClick={() => setPaymentFilter(s)} testid={`filter-payment-${s}`}>{s}</Chip>
          ))}
        </FilterRow>
        <FilterRow label="Method">
          {[
            { v: "all", l: "all" },
            { v: "razorpay", l: "razorpay" },
            { v: "cod", l: "cod" },
            { v: "cod_partial", l: "cod partial" },
          ].map((s) => (
            <Chip key={s.v} active={methodFilter === s.v} onClick={() => setMethodFilter(s.v)} testid={`filter-method-${s.v}`}>{s.l}</Chip>
          ))}
        </FilterRow>
        <FilterRow label="Fulfilment">
          {[
            { v: "all", l: "all" },
            { v: "unfulfilled", l: "unfulfilled" },
            { v: "fulfilled", l: "fulfilled" },
            { v: "shipped", l: "shipped" },
            { v: "delivered", l: "delivered" },
            { v: "cancelled", l: "cancelled" },
          ].map((s) => (
            <Chip key={s.v} active={fulfillmentFilter === s.v} onClick={() => setFulfillmentFilter(s.v)} testid={`filter-fulfillment-${s.v}`}>{s.l}</Chip>
          ))}
        </FilterRow>
        <FilterRow label="Date">
          {[
            { v: "all", l: "all time" },
            { v: "today", l: "today" },
            { v: "7d", l: "last 7 days" },
            { v: "30d", l: "last 30 days" },
            { v: "custom", l: "custom" },
          ].map((s) => (
            <Chip key={s.v} active={datePreset === s.v} onClick={() => setDatePreset(s.v)} testid={`filter-date-${s.v}`}>{s.l}</Chip>
          ))}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="field !py-1.5 text-xs" data-testid="filter-date-from" />
              <span className="text-xs text-ink-500">to</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="field !py-1.5 text-xs" data-testid="filter-date-to" />
            </div>
          )}
        </FilterRow>
      </div>

      <div className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm font-body min-w-[920px]">
          <thead>
            <tr className="bg-blush-50/40 text-left text-[11px] uppercase tracking-[0.18em] text-ink-500">
              <th className="px-4 py-3 w-12 text-center">#</th>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Fulfilment</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-6 py-10 text-center text-ink-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-6 py-10 text-center text-ink-500">No orders match these filters.</td></tr>
            ) : filtered.map((o, idx) => (
              <tr key={o.id} className="border-t border-blush-100 hover:bg-blush-50/40 transition">
                <td className="px-4 py-3 text-center text-ink-500 text-xs">{idx + 1}</td>
                <td className="px-4 py-3">
                  <Link to={`/admin/orders/${o.id}`} className="text-blush-600 hover:underline">
                    {o.order_number}
                  </Link>
                </td>
                <td className="px-4 py-3 text-ink-900">{o.customer_name}</td>
                <td className="px-4 py-3 text-ink-700">{o.items?.reduce((s, i) => s + i.quantity, 0)}</td>
                <td className="px-4 py-3 text-ink-900">{fmt(o.total)}</td>
                <td className="px-4 py-3 text-ink-700 text-xs uppercase tracking-[0.16em]">{o.payment_method || "—"}</td>
                <td className="px-4 py-3"><Pill value={o.payment_status} /></td>
                <td className="px-4 py-3"><Pill value={o.fulfillment_status} /></td>
                <td className="px-4 py-3 text-ink-500 text-xs">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleDelete(o)}
                    disabled={deletingId === o.id}
                    title="Delete order"
                    data-testid={`delete-order-${o.order_number}`}
                    className="p-1.5 rounded-full text-ink-400 hover:text-rose-600 hover:bg-rose-50 transition disabled:opacity-40"
                  >
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] uppercase tracking-[0.18em] text-ink-500 font-body w-20 shrink-0">{label}</span>
      {children}
    </div>
  );
}

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-[11px] font-body uppercase tracking-[0.16em] transition ${
        active ? "bg-blush-600 text-white" : "bg-white border border-blush-100 text-ink-700 hover:border-blush-300"
      }`}
    >
      {children}
    </button>
  );
}

export function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/orders/${id}`);
      setOrder(data);
      setTrackingId(data.tracking_id || "");
      setTrackingUrl(data.tracking_url || "");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/orders/${id}/status`, {
        status,
        tracking_id: trackingId,
        tracking_url: trackingUrl,
      });
      const willEmail = ["shipped", "delivered", "cancelled"].includes(status);
      toast.success(willEmail ? `Marked ${status} · customer notified` : "Updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteOrder = async () => {
    if (!order) return;
    if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success(`Order ${order.order_number} deleted`);
      navigate("/admin/orders");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const downloadSlip = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/orders/${id}/slip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-slip-${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createShipment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/shiprocket/create-shipment/${id}`);
      toast.success("Shipment created", { description: `AWB: ${data.shiprocket?.awb_code || "—"}` });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 font-script text-3xl text-blush-600">loading…</div>;
  if (!order) return <div className="p-10">Order not found.</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="admin-order-detail">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blush-600 mb-6">
        <ChevronLeft size={14} strokeWidth={1.6} /> Back to orders
      </Link>

      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="font-script text-3xl text-blush-400">order</span>
          <h1 className="font-heading text-4xl text-ink-900 flex items-center gap-3">
            {order.order_number}
            <button
              onClick={() => { navigator.clipboard.writeText(order.order_number); toast.success("Copied"); }}
              className="text-ink-500 hover:text-blush-600"
            >
              <Copy size={16} strokeWidth={1.6} />
            </button>
          </h1>
          <p className="mt-2 text-sm font-body text-ink-500">
            Placed {(order.created_at || "").slice(0, 19).replace("T", " · ")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill value={order.payment_status} />
          <Pill value={order.fulfillment_status} />
          <button
            onClick={downloadSlip}
            disabled={busy}
            data-testid="download-slip-btn"
            className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-blush-200 bg-blush-50 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-blush-700 hover:bg-blush-100 transition disabled:opacity-50"
          >
            <FileDown size={12} strokeWidth={1.6} /> Download slip
          </button>
          <button
            onClick={deleteOrder}
            disabled={busy}
            data-testid="delete-order-btn"
            className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-rose-700 hover:bg-rose-100 transition disabled:opacity-50"
          >
            <Trash2 size={12} strokeWidth={1.6} /> Delete order
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Items">
            <ul className="divide-y divide-blush-100">
              {order.items.map((i, idx) => (
                <li key={idx} className="py-3 flex items-center gap-3">
                  {i.image && <img src={i.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                  <div className="flex-1">
                    <p className="font-body text-ink-900">{i.name}</p>
                    <p className="text-xs text-ink-500">Size {i.size} · Qty {i.quantity}</p>
                  </div>
                  <p className="font-body text-ink-900">{fmt(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-1 text-sm font-body text-ink-700">
              <div className="flex justify-between"><dt>Subtotal</dt><dd>{fmt(order.subtotal)}</dd></div>
              <div className="flex justify-between"><dt>Shipping</dt><dd>{fmt(order.shipping)}</dd></div>
              <div className="flex justify-between"><dt>Tax</dt><dd>{fmt(order.tax)}</dd></div>
              <div className="flex justify-between font-heading text-lg pt-2 border-t border-blush-100"><dt>Total</dt><dd>{fmt(order.total)}</dd></div>
            </dl>
          </Card>

          <Card title="Shipping address">
            <p className="font-body text-ink-900">{order.address.first_name} {order.address.last_name}</p>
            <p className="font-body text-ink-700 text-sm mt-1">
              {order.address.address}{order.address.apartment ? `, ${order.address.apartment}` : ""}<br />
              {order.address.city}, {order.address.state} {order.address.pincode}<br />
              {order.address.country}
            </p>
            <p className="mt-3 text-xs font-body text-ink-500">
              {order.address.email} · {order.address.phone}
            </p>
          </Card>

          {order.utm && Object.keys(order.utm).length > 0 && (
            <Card title="Attribution">
              <dl className="grid grid-cols-2 gap-3 text-sm font-body">
                {Object.entries(order.utm).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{k}</dt>
                    <dd className="text-ink-900 truncate">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card title="Payment">
            <p className="text-sm font-body text-ink-700">Method: <span className="text-ink-900">{order.payment_method}</span></p>
            {order.payment_method === "cod_partial" && (
              <p className="text-sm font-body text-ink-700 mt-1">
                Paid online: <span className="text-ink-900">{fmt(order.cod_partial_amount)}</span>
                {" · "}Due on delivery: <span className="text-ink-900">{fmt(order.total - (order.cod_partial_amount || 0))}</span>
              </p>
            )}
            {order.razorpay_order_id && (
              <p className="text-xs text-ink-500 mt-1 break-all">RZP order: {order.razorpay_order_id}</p>
            )}
            {order.razorpay_payment_id && (
              <p className="text-xs text-ink-500 mt-1 break-all">RZP pay: {order.razorpay_payment_id}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {order.payment_status !== "paid" && (
                <button onClick={() => updateStatus("paid")} disabled={busy} className="btn-primary text-xs">Mark paid</button>
              )}
              {order.payment_status !== "failed" && (
                <button onClick={() => updateStatus("failed")} disabled={busy} className="btn-ghost-rose text-xs">Mark failed</button>
              )}
            </div>
          </Card>

          <Card title="Fulfilment">
            <input
              className="field"
              placeholder="Tracking ID / AWB"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              data-testid="admin-order-tracking"
            />
            <input
              className="field mt-2"
              placeholder="Tracking URL (e.g. https://shiprocket.co/tracking/AWB123)"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              data-testid="admin-order-tracking-url"
            />
            <p className="text-[11px] text-ink-500 mt-1.5 leading-relaxed">
              Tip: paste the tracking link before clicking <strong>Mark shipped</strong> — it will be included in the customer email.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => updateStatus("shipped")} disabled={busy} className="btn-primary text-xs" data-testid="mark-shipped-btn">
                <Truck size={12} strokeWidth={1.6} className="inline mr-1" /> Mark shipped
              </button>
              <button onClick={() => updateStatus("delivered")} disabled={busy} className="btn-ghost-rose text-xs" data-testid="mark-delivered-btn">
                Mark delivered
              </button>
              <button onClick={() => updateStatus("cancelled")} disabled={busy} className="btn-ghost-rose text-xs" data-testid="mark-cancelled-btn">
                Cancel order
              </button>
            </div>
            {order.payment_status === "paid" && order.fulfillment_status === "unfulfilled" && (
              <button
                onClick={createShipment}
                disabled={busy}
                data-testid="admin-shiprocket-create"
                className="btn-maroon mt-3 w-full inline-flex items-center justify-center gap-2 text-xs"
              >
                <Truck size={12} strokeWidth={1.6} /> Create Shiprocket shipment
              </button>
            )}
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-blush-600"
              >
                Track on Shiprocket <ExternalLink size={11} strokeWidth={1.6} />
              </a>
            )}
          </Card>

          <Card title="Customer">
            <p className="font-body text-ink-900">{order.customer_name}</p>
            <p className="text-xs text-ink-500 mt-1">{order.customer_email}</p>
            <p className="text-xs text-ink-500">{order.customer_phone}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-3xl bg-white border border-blush-100 p-6">
      <h3 className="font-heading text-xl text-ink-900 mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Pill({ value }) {
  const colours = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
    refunded: "bg-slate-50 text-slate-700 border-slate-200",
    unfulfilled: "bg-blush-50 text-blush-700 border-blush-200",
    shipped: "bg-blue-50 text-blue-700 border-blue-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const cls = colours[value] || "bg-blush-50 text-blush-700 border-blush-200";
  return (
    <span className={`inline-block rounded-full border px-3 py-0.5 text-[11px] uppercase tracking-[0.18em] ${cls}`}>
      {value || "—"}
    </span>
  );
}
